import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, getcountries, getseriesType } from '../../../services/event/abstractReceipt/abReviewersServices';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';
import { validateImageFile } from '../../../utils/fileValidation';

const subDomain = localStorage.getItem('sub_domain') || '';

// ── Parse remarks safely ─────────────────────────────────────────────────────
// Handles: null, JS array (Sequelize JSON), JSON string, double-stringified
const parseRemarks = (raw) => {
  if (!raw) return [];
  try {
    if (Array.isArray(raw)) return raw;
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const Abreviewers = () => {
  const [dataList, setDataList]             = useState([]);
  const [form, setForm]                     = useState({
    id: '', sub_domain: subDomain, series: '', reviewer_id: '',
    reviewer_name: '', reviewer_affiliation: '', reviewer_country: '',
    uploadedfile: null, status: false, approval: false,
  });
  const [modalType, setModalType]           = useState('');
  const [showModal, setShowModal]           = useState(false);
  const [searchText, setSearchText]         = useState('');
  const [currentPage, setCurrentPage]       = useState(1);
  const [countries, setcountries]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [errors, setErrors]                 = useState({});
  const [seriesValue, setSeriesValue]       = useState('');
  const [remarks, setRemarks]               = useState('');
  const [remarksHistory, setRemarksHistory] = useState([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ sub_domain: subDomain });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchseriesType = useCallback(async () => {
    try {
      const data = await getseriesType({ sub_domain: subDomain });
      if (data && data.length > 0) setSeriesValue(data[0].series || '');
    } catch (error) {
      console.error('Failed to fetch series info:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchData();
      await fetchseriesType();
      await new Promise((r) => setTimeout(r, 1000));
      setLoading(false);
    };
    init();
  }, [fetchData, fetchseriesType]);

  useEffect(() => {
    const fetchcountries = async () => {
      try {
        const data = await getcountries();
        setcountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchcountries();
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];
      validateImageFile(file)
        .then(() => {
          setForm((prev) => ({ ...prev, [name]: file }));
          setErrors((prev) => ({ ...prev, uploadedfile: '' }));
        })
        .catch((err) => {
          setForm((prev) => ({ ...prev, [name]: null }));
          setErrors((prev) => ({ ...prev, uploadedfile: err }));
        });
      return;
    }
    let val = value;
    if (name === 'status') val = value === '1';
    setForm((prev) => ({ ...prev, [name]: val }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.uploadedfile && modalType === 'add')
      newErrors.uploadedfile = 'Signature file is required';
    if (!form.reviewer_name.trim())
      newErrors.reviewer_name = 'Reviewer name is required';
    if (!form.reviewer_affiliation.trim())
      newErrors.reviewer_affiliation = 'Reviewer affiliation is required';
    if (!form.reviewer_country.trim())
      newErrors.reviewer_country = 'Country is required';

    if (modalType === 'edit' && !remarks.trim()) {
      newErrors.remarks = 'Remarks is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isSeriesMismatch = modalType === 'edit' && form.series !== seriesValue;

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    if (!validateForm()) return;
    e.preventDefault();

    let updatedForm = { ...form };

    if (modalType === 'edit') {
      if (updatedForm.status === true) updatedForm.approval = false;
      if (updatedForm.series !== seriesValue) updatedForm.series = seriesValue;
    }

    // ── Build remark string ────────────────────────────────────────────────
    // If series changed: prefix "Series updated from X to Y"
    // If user also typed something: append " | <user remark>"
    // If no series change: store only what user typed (if anything)
    const updatedRemarks = [...remarksHistory];
    const seriesPrefix   = isSeriesMismatch
      ? `Series updated from ${form.series} to ${seriesValue}`
      : '';
    const userRemark     = remarks.trim();

    let finalRemark = '';
    if (seriesPrefix && userRemark) {
      finalRemark = `${seriesPrefix} | ${userRemark}`;
    } else if (seriesPrefix) {
      finalRemark = seriesPrefix;
    } else if (userRemark) {
      finalRemark = userRemark;
    }

    if (finalRemark) {
      updatedRemarks.push({
        remark:    finalRemark,
        timestamp: new Date().toISOString(),
      });
    }

    const formData = new FormData();
    formData.append('id',                   updatedForm.id || '');
    formData.append('reviewer_id',          updatedForm.reviewer_id || '');
    formData.append('sub_domain',           updatedForm.sub_domain);
    formData.append('series',               updatedForm.series);
    formData.append('reviewer_name',        updatedForm.reviewer_name);
    formData.append('reviewer_affiliation', updatedForm.reviewer_affiliation);
    formData.append('reviewer_country',     updatedForm.reviewer_country);
    formData.append('status',               updatedForm.status ? 1 : 0);
    formData.append('approval',             updatedForm.approval ? 1 : 0);
    formData.append('remarks',              JSON.stringify(updatedRemarks));
    if (updatedForm.uploadedfile) formData.append('uploadedfile', updatedForm.uploadedfile);

    try {
      if (modalType === 'add')       await addData(formData);
      else if (modalType === 'edit') await updateData(formData);
      fetchData();
      resetModal();
      Swal.fire({
        icon: 'success', title: 'Success',
        text: `Reviewers ${modalType === 'add' ? 'added' : 'updated'} successfully!`,
        timer: 2000, showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error during form submission:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong while submitting the form!' });
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setRemarks('');
    setRemarksHistory([]);
  };

  // ── Open modal ─────────────────────────────────────────────────────────────
  const openModal = (
    type,
    data = {
      id: '', sub_domain: subDomain, series: seriesValue || '',
      reviewer_id: '', reviewer_name: '', reviewer_affiliation: '',
      reviewer_country: '', uploadedfile: null, status: false,
      approval: false, remarks: null,
    }
  ) => {
    setErrors({});
    setModalType(type);
    setRemarksHistory(parseRemarks(data.remarks));
    setRemarks(''); // always blank — series prefix is built on submit
    setForm({ ...data, status: !!data.status, approval: !!data.approval });
    setShowModal(true);
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  const getCountryName = (code) => {
    const c = countries.find((c) => c.code === code);
    return c ? c.name : code;
  };

  const filteredData = useMemo(() => {
    return dataList
      .filter((d) =>
        d.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
        d.reviewer_name.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [dataList, searchText]);

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    { name: '#',       selector: (row, i) => (currentPage - 1) * 10 + (i + 1), sortable: false, width: '80px' },
    { name: 'Name',    selector: (row) => row.reviewer_name,                    sortable: true,  wrap: true },
    { name: 'Country', selector: (row) => getCountryName(row.reviewer_country), sortable: true,  wrap: true },
    {
      name: 'Signature',
      selector: (row) => <img src={row.reviewer_signature} width="100" alt={row.reviewer_affiliation} />,
      sortable: false, wrap: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span className={`badge ${Number(row.status) === 1 ? 'bg-success' : 'bg-danger'}`}>
          {Number(row.status) === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true, wrap: true,
    },
    {
      name: 'Approval',
      cell: (row) => (
        <span className={`badge ${Number(row.approval) === 1 ? 'bg-success' : 'bg-danger'}`}>
          {Number(row.approval) === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true, wrap: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button variant="warning" size="sm" onClick={() => openModal('edit', row)}>Edit</Button>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">
                    Receipt Generation / Abstract Receipt / Configurations / Reviewers
                  </p>
                </div>
                <div className="col-4 text-end">
                  <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>Add</button>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
              />

              {/* ── Modal ─────────────────────────────────────────────────── */}
              <Modal show={showModal} backdrop="static" className="modal-lg" onHide={resetModal}>
                <Modal.Header closeButton>
                  <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'} Reviewer</Modal.Title>
                </Modal.Header>

                <Modal.Body>

                  {/* ── Series mismatch notification bar (top of modal) ── */}
                  {isSeriesMismatch && (
                    <Alert variant="warning" className="mb-3 py-2 d-flex align-items-center gap-2">
                      <span>⚠️</span>
                      <span>
                        Series has changed from{' '}
                        <strong>{form.series}</strong> to{' '}
                        <strong>{seriesValue}</strong>.
                        This will be updated automatically on save.
                      </span>
                    </Alert>
                  )}

                  <Form>
                    <div className="row">

                      {/* Subdomain */}
                      <Form.Group className="col-md-3 mb-3">
                        <Form.Label className="fs-5 text-dark">Subdomain</Form.Label>
                        <Form.Control type="text" name="sub_domain" value={form.sub_domain} readOnly />
                      </Form.Group>

                      {/* Series */}
                      <Form.Group className="col-md-3 mb-3">
                        <Form.Label className="fs-5 text-dark">Series</Form.Label>
                        <Form.Control type="text" name="series" value={form.series} readOnly />
                      </Form.Group>

                      {/* Country */}
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Country <span className="fs-4 text-danger"> * </span>
                        </Form.Label>
                        <Form.Control
                          as="select" className="form-select mb-3"
                          name="reviewer_country" value={form.reviewer_country}
                          onChange={handleChange} isInvalid={!!errors.reviewer_country}
                        >
                          <option value="">Select</option>
                          {countries.map((c) => (
                            <option key={c.name} value={c.code}>{c.name}</option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">{errors.reviewer_country}</Form.Control.Feedback>
                      </Form.Group>

                      {/* Reviewer Name */}
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Reviewer Name <span className="fs-4 text-danger"> * </span>
                        </Form.Label>
                        <Form.Control
                          type="text" name="reviewer_name" value={form.reviewer_name}
                          onChange={handleChange} isInvalid={!!errors.reviewer_name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.reviewer_name}</Form.Control.Feedback>
                      </Form.Group>

                      {/* Reviewer Affiliation */}
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Reviewer Affiliation <span className="fs-4 text-danger"> * </span>
                        </Form.Label>
                        <Form.Control
                          type="text" name="reviewer_affiliation" value={form.reviewer_affiliation}
                          onChange={handleChange} isInvalid={!!errors.reviewer_affiliation}
                        />
                        <Form.Control.Feedback type="invalid">{errors.reviewer_affiliation}</Form.Control.Feedback>
                      </Form.Group>

                      {/* Status (edit only) */}
                      {modalType === 'edit' && (
                        <Form.Group className="col-md-6 mb-3">
                          <Form.Label>Status</Form.Label>
                          <Form.Select name="status" value={form.status ? '1' : '0'} onChange={handleChange}>
                            <option>Select</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </Form.Select>
                        </Form.Group>
                      )}

                      {/* Signature */}
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Reviewer Signature{' '}
                          <small className="text-primary">
                            (Dimension:300x100 | Size:5kb to 30kb){' '}
                            <span className="fs-4 text-danger"> * </span>
                          </small>
                          <br />
                          <small>(upload your signature in <b>black color only</b>)</small>
                        </Form.Label>
                        <Form.Control
                          type="file" name="uploadedfile"
                          onChange={handleChange} isInvalid={!!errors.uploadedfile}
                        />
                        <Form.Control.Feedback type="invalid">{errors.uploadedfile}</Form.Control.Feedback>
                      </Form.Group>

                      {/* ── Remarks History (edit only, only if entries exist) ── */}
                      {modalType === 'edit' && remarksHistory.length > 0 && (
                        <div className="col-md-12 mb-3">
                          <Form.Label className="fs-5 text-dark fw-semibold">
                            Remarks History
                            <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                              {remarksHistory.length}
                            </span>
                          </Form.Label>
                          <div
                            className="border rounded"
                            style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                          >
                            {[...remarksHistory].reverse().map((entry, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 border-bottom"
                                style={{ borderLeft: '3px solid #0d6efd' }}
                              >
                                <small className="text-muted d-block mb-1">
                                  🕒 {formatTimestamp(entry.timestamp)}
                                </small>
                                <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>
                                  {entry.remark}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── Remarks textarea (edit only) ── */}
                      {modalType === 'edit' && (
                        <Form.Group className="col-md-12 mb-3">
                          <Form.Label className="fs-5 text-dark">Remarks</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={remarks}
                            placeholder="Enter remarks (optional)"
                            onChange={(e) => setRemarks(e.target.value)} isInvalid={!!errors.remarks}
                          />
                          {isSeriesMismatch && (
                            <Form.Text className="text-muted">
                              Series change will be auto-prefixed before your remark on save.
                            </Form.Text>
                          )}
                          <Form.Control.Feedback type="invalid">{errors.remarks}</Form.Control.Feedback>
                        </Form.Group>
                      )}

                    </div>
                  </Form>
                </Modal.Body>

                <Modal.Footer>
                  <Button variant="secondary" onClick={resetModal}>Cancel</Button>
                  <Button variant="primary" onClick={handleSubmit}>
                    {modalType === 'add' ? 'Add' : 'Save'}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Abreviewers;
